import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, TrendingUp, ArrowRight } from 'lucide-react';
import { podcastInfo } from '../mock';
import { api } from '../lib/api';

const TRENDING_SLUGS = [
  'first-female-afp-spokesperson-ai-leadership',
  'the-global-challenge-of-ai-regulation',
  'taxing-the-future-how-ai-is-transforming-global-taxation',
];

const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const REVEAL = { type: 'spring', stiffness: 520, damping: 26 };

const Hero = () => {
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.get('/posts')
      .then((res) => {
        const bySlug = new Map(res.data.map((p) => [p.slug, p]));
        const picked = TRENDING_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
        setTrending(picked);
      })
      .catch(() => setTrending([]));
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-24 pb-16 px-4 sm:px-6 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#EF5B5B]/10 via-transparent to-transparent" />
      
      <div className="relative max-w-6xl mx-auto w-full">
        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-4 sm:mb-6"
          >
            <span className="px-4 py-2 rounded-full glass text-sm font-medium text-gray-300">
              New Episodes Every Monday
            </span>
          </motion.div>
          
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-4 sm:mb-6 tracking-tight">
            Your Unfair Advantage{' '}
            <br className="hidden sm:block" />
            in the <span className="gradient-text">Age of AI</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8 sm:mb-10">
            Powerful insights from CEOs, innovators, and elite industry leaders redefining
            business, leadership, and technology. Hosted by Jeslyn Allison Rancap Jerota.
          </p>
        </motion.div>

        {/* Video + Trending sidebar */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-8 sm:mb-10 grid lg:grid-cols-[1fr_300px] gap-6 lg:gap-8 items-start"
        >
          <div className="glass-strong rounded-2xl sm:rounded-3xl p-3 sm:p-6 glow-red">
            <div className="video-container">
              <iframe
                src={`https://www.youtube.com/embed/${podcastInfo.latestEpisodeYoutubeId}?rel=0&modestbranding=1`}
                title="Latest Episode"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-xl sm:rounded-2xl"
              />
            </div>
          </div>

          {trending.length > 0 && (
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...REVEAL, delay: 0.6 }}
              className="glass-strong rounded-2xl sm:rounded-3xl p-6 border border-[#EF5B5B]/20"
              data-testid="hero-trending-panel"
              style={{
                background:
                  'radial-gradient(120% 90% at 100% 0%, rgba(239,91,91,0.14), transparent 55%),' +
                  'linear-gradient(180deg, rgba(20,10,12,0.85), rgba(10,10,10,0.9))',
              }}
            >
              <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/10">
                <TrendingUp className="w-4 h-4 text-[#EF5B5B]" />
                <h3 className="text-[10px] font-black uppercase tracking-[0.32em] text-gray-200">Trending</h3>
              </div>
              <ol className="space-y-5">
                {trending.map((post, i) => (
                  <motion.li
                    key={post.slug}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ ...REVEAL, delay: 0.7 + i * 0.08 }}
                    whileHover={{ x: 3 }}
                    className="flex items-start gap-3"
                  >
                    <span className="text-xl font-black text-[#EF5B5B] w-6 shrink-0 leading-none pt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <Link
                      to={`/blog/${post.slug}`}
                      data-testid={`hero-trending-${post.slug}`}
                      className="text-[13.5px] leading-snug text-gray-100 hover:text-[#EF5B5B] smooth font-semibold"
                    >
                      {post.title}
                    </Link>
                  </motion.li>
                ))}
              </ol>
              <Link
                to="/episodes"
                data-testid="hero-trending-view-all"
                className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold text-[#EF5B5B] hover:gap-2.5 smooth"
              >
                See all episodes <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.aside>
          )}
        </motion.div>

        {/* Platform Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={podcastInfo.spotifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full sm:w-auto flex items-center justify-center gap-3 px-6 sm:px-8 py-4 btn-red rounded-full font-semibold text-base sm:text-lg"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            Listen on Spotify
          </a>
          
          <a
            href={podcastInfo.applePodcastsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full sm:w-auto flex items-center justify-center gap-3 px-6 sm:px-8 py-4 glass-strong hover:bg-[#EF5B5B]/10 border-2 border-[#EF5B5B]/30 hover:border-[#EF5B5B] rounded-full font-semibold smooth text-base sm:text-lg"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.182c5.423 0 9.818 4.395 9.818 9.818 0 5.423-4.395 9.818-9.818 9.818-5.423 0-9.818-4.395-9.818-9.818 0-5.423 4.395-9.818 9.818-9.818zM12 5.455c-1.767 0-3.273 1.506-3.273 3.272 0 1.767 1.506 3.273 3.273 3.273s3.273-1.506 3.273-3.273c0-1.766-1.506-3.272-3.273-3.272zm0 1.454c1.01 0 1.818.81 1.818 1.818 0 1.01-.809 1.819-1.818 1.819-1.01 0-1.818-.81-1.818-1.819 0-1.009.809-1.818 1.818-1.818zm0 4.364c-2.423 0-4.364 1.94-4.364 4.363v2.182c0 .402.326.727.728.727h7.272c.402 0 .728-.325.728-.727v-2.182c0-2.423-1.941-4.363-4.364-4.363z"/>
            </svg>
            Listen on Apple Podcasts
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
